import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';

const siteUrl = typeof __VITE_SITE_URL__ !== 'undefined' ? __VITE_SITE_URL__ : 'https://cssspreadsheet.monster';

const topFinds = [
  { category: 'Shoes', item: 'Nike Air Force 1 Low — Top Tier Batch', why: 'Near-perfect shape, premium leather feel, and consistently excellent stitching across all sizes. The most-reviewed shoe in the entire spreadsheet.' },
  { category: 'Hoodies/Sweaters', item: 'Balenciaga Campaign Hoodie — Heavyweight Cotton', why: 'Incredible weight and drape that matches retail almost perfectly. The embroidery on the chest logo is dense and dimensional.' },
  { category: 'T-Shirts', item: 'Essentials Fear of God Tee — 3-Pack Bundle', why: 'The best value buy in the spreadsheet. Soft, oversized fit with accurate silicone-print logos that have survived dozens of washes.' },
  { category: 'Jackets', item: 'Arc\'teryx Beta LT Shell — Technical Batch', why: 'Surprisingly excellent waterproofing and zipper quality. The hood adjustment system works exactly like the retail version.' },
  { category: 'Bags', item: 'Louis Vuitton Avenue Sling Bag — Mirror Quality', why: 'Canvas alignment, hardware weight, and stitching placement are all virtually indistinguishable from the authentic piece.' },
  { category: 'Accessories', item: 'Chrome Hearts Dagger Pendant — Sterling Silver', why: 'Solid weight, crisp engraving details, and a beautifully oxidized finish that develops character with wear.' },
  { category: 'Belts', item: 'Hermès H Belt Kit — Full Packaging', why: 'Comes with the complete box, dust bag, and authenticity card presentation. The leather develops a rich patina within weeks.' },
  { category: 'Perfumes', item: 'Maison Margiela Replica Jazz Club — 100ml', why: 'Shockingly accurate scent profile with excellent longevity. The bottle and packaging are indistinguishable from retail.' },
];

export default function BestFindsPage() {
  const navigate = useNavigate();

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-24 md:pt-28 pb-10 md:pb-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <button onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-flex items-center gap-1 text-xs font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-arrow-left-line"></i>
                  Home
                </button>
                <span className="text-foreground-300">/</span>
                <button onClick={() => { navigate('/cssbuy-shopping-guide'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-flex items-center gap-1 text-xs font-medium text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                  Shopping Guide
                </button>
                <span className="text-foreground-300">/</span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">Best Finds</span>
              </div>
              <span className="inline-block px-3 py-1 text-xs font-semibold text-accent-700 bg-accent-100 rounded-full mb-4 whitespace-nowrap">
                Curated July 2026
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-foreground-950 leading-tight mb-4">
                Best CSSBuy Spreadsheet Finds of 2026: The Community&apos;s Most Loved Items
              </h1>
              <p className="text-sm text-foreground-500 leading-relaxed">
                A handpicked selection of the highest-rated, most-reviewed items from the CSSBuy spreadsheet this year — organized by category with detailed notes on why each pick stands out from the crowd.
              </p>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="pb-8 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Curated%20collection%20of%20the%20most%20popular%20fashion%20items%20displayed%20on%20a%20warm%20oak%20table%20including%20white%20sneakers%20a%20grey%20hoodie%20a%20leather%20crossbody%20bag%20silver%20jewelry%20and%20a%20designer%20fragrance%20bottle%2C%20soft%20golden%20hour%20window%20light%20creating%20romantic%20warm%20highlights%2C%20handwritten%20rating%20cards%20with%20five%20star%20reviews%20placed%20next%20to%20each%20item%2C%20fashion%20blogger%20flat%20lay%20with%20intentional%20styling%20and%20organic%20composition%2C%20aspirational%20lifestyle%20aesthetic%20with%20premium%20feel&width=1200&height=600&seq=best-finds-hero-v2&orientation=landscape"
                  alt="Best CSSBuy Spreadsheet Finds of 2026 — Community-curated top picks"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom space-y-5 text-foreground-700 leading-relaxed">
                <p className="text-base">
                  The <strong>CSSBuy spreadsheet</strong> contains thousands of listings, and even experienced buyers can feel overwhelmed by the sheer volume of options. To save you hours of scrolling and cross-referencing, we have compiled this curated list of the absolute best finds of 2026 — items that the community has rated highly across multiple criteria including quality, accuracy, value for money, and seller reliability.
                </p>
                <p className="text-base">
                  Every item on this list has been verified by multiple community members who have purchased, received QC photos, and in most cases, worn or used the product extensively before providing feedback. These are not just items that looked good in listing photos — these are products that have proven themselves in the real world.
                </p>
              </div>

              {/* Top Finds Table */}
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
                The Complete Best Finds List
              </h2>
              <div className="space-y-4">
                {topFinds.map((find, i) => (
                  <div key={i} className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/70 hover:border-accent-300 transition-all duration-300">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-500 text-background-50 font-heading text-lg font-semibold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs font-semibold text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                            {find.category}
                          </span>
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-1">
                          {find.item}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {find.why}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Why These Matter */}
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950 mt-12 mb-5">
                How These Picks Were Selected
              </h2>
              <div className="prose-custom space-y-4 text-foreground-700 leading-relaxed">
                <p className="text-base">
                  Our selection methodology is straightforward and transparent. Each item on this list earned its spot by meeting at least three of the following five criteria:
                </p>
                <ol className="list-decimal pl-5 space-y-3">
                  <li className="text-base"><strong>Multiple verified purchases</strong> — at least three different community members have bought and reviewed the item.</li>
                  <li className="text-base"><strong>Positive QC photo history</strong> — recent QC photos consistently show good quality with no recurring flaws.</li>
                  <li className="text-base"><strong>Seller reliability</strong> — the seller has a track record of shipping correct items on time and handling exchanges professionally.</li>
                  <li className="text-base"><strong>Value for money</strong> — the item delivers quality that matches or exceeds its price point relative to competing batches.</li>
                  <li className="text-base"><strong>Community consensus</strong> — the item is frequently recommended in community forums, Discord channels, and review threads.</li>
                </ol>
                <p className="text-base">
                  Every item in the <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-accent-600 hover:text-accent-700 underline cursor-pointer"><strong>CSSBuy spreadsheet</strong></a> has its own dedicated entry with direct product links and the latest batch information. We update this best finds list monthly to reflect new community discoveries and to remove items whose quality has declined in recent batches. Bookmark this page and check back regularly — the spreadsheet moves fast, and the best finds change with it.
                </p>
              </div>

              {/* Pro Tips */}
              <div className="mt-10 bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70">
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-4">
                  Pro Tips for Finding Your Own Hidden Gems
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-lightbulb-line text-accent-600 text-xs"></i>
                    </div>
                    <span className="text-sm text-foreground-700 leading-relaxed"><strong>Sort by newest first.</strong> The most recently added listings often contain items that are still fully in stock and have not been over-ordered by the community yet.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-lightbulb-line text-accent-600 text-xs"></i>
                    </div>
                    <span className="text-sm text-foreground-700 leading-relaxed"><strong>Check the Shoes category first.</strong> It is the most active section of the spreadsheet and often the first place where new trending items appear before spreading to other categories.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-5 h-5 rounded-md bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-lightbulb-line text-accent-600 text-xs"></i>
                    </div>
                    <span className="text-sm text-foreground-700 leading-relaxed"><strong>Read the comments column.</strong> Experienced buyers leave invaluable notes about sizing quirks, batch comparisons, and which agent offers the best shipping rates for specific items.</span>
                  </li>
                </ul>
              </div>

              {/* Internal Links */}
              <div className="mt-12 bg-accent-50/50 rounded-2xl p-6 border border-accent-200/50">
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                  Explore More CSSBuy Resources
                </h3>
                <ul className="space-y-2">
                  <li>
                    <button onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-flex items-center gap-1.5 text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-home-line text-sm"></i>
                      CSSSpreadsheet Homepage
                    </button>
                  </li>
                  <li>
                    <button onClick={() => { navigate('/cssbuy-shopping-guide'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-flex items-center gap-1.5 text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-shopping-bag-line text-sm"></i>
                      Shopping Guide Hub
                    </button>
                  </li>
                  <li>
                    <button onClick={() => { navigate('/cssbuy-spreadsheet-guide'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-flex items-center gap-1.5 text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-guide-line text-sm"></i>
                      CSSBuy Spreadsheet Guide
                    </button>
                  </li>
                  <li>
                    <button onClick={() => { navigate('/cssbuy-qc-tutorial'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-flex items-center gap-1.5 text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap">
                      <i className="ri-search-eye-line text-sm"></i>
                      QC Photo Tutorial
                    </button>
                  </li>
                </ul>
              </div>

              {/* CTA */}
              <div className="mt-10 text-center bg-foreground-950 rounded-2xl p-8 md:p-10">
                <h3 className="font-heading text-2xl md:text-3xl font-semibold text-background-50 mb-3">
                  Start Finding Your Own Best Picks
                </h3>
                <p className="text-sm text-background-50/70 mb-6 max-w-md mx-auto">
                  These eight items are just the beginning. The full CSSBuy spreadsheet contains hundreds more community-verified finds across fourteen categories.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold text-foreground-950 bg-primary-400 hover:bg-primary-300 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                    Browse Full Spreadsheet
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                  <button onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-flex items-center gap-2 px-7 py-3 text-sm font-medium text-background-50 border border-background-50/30 hover:bg-background-50/10 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                    Back to Homepage
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              'headline': "Best CSSBuy Spreadsheet Finds of 2026: The Community's Most Loved Items",
              'description': 'A handpicked selection of the highest-rated, most-reviewed items from the CSSBuy spreadsheet this year — organized by category with detailed notes on why each pick stands out from the crowd.',
              'url': siteUrl + '/cssbuy-best-finds-2026',
              'datePublished': '2026-07-06',
              'dateModified': '2026-07-06',
              'author': {
                '@type': 'Organization',
                'name': 'CSSSpreadsheet',
              },
              'publisher': {
                '@type': 'Organization',
                'name': 'CSSSpreadsheet',
                'url': siteUrl,
              },
              'mainEntityOfPage': {
                '@type': 'WebPage',
                '@id': siteUrl + '/cssbuy-best-finds-2026',
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}